/**
 * Storage utility for Specialist Referrals
 * HIPAA Compliance: Uses encrypted storage for PHI
 * Regulation: 45 CFR 164.312(a)(2)(iv) - Encryption and Decryption
 */

import secureStorage from '../../../utils/security/secureStorage';

const STORAGE_KEY = "specialist_referrals";

/**
 * Generate a unique ID for new referrals
 */
const generateId = () => {
  return `REF-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get all referrals from secure storage
 */
export const getAllReferrals = () => {
  try {
    const data = secureStorage.getItem(STORAGE_KEY);
    if (!data) return [];
    return Array.isArray(data) ? data : [];
  } catch (err) {
    // HIPAA: Don't log error details
    console.error("Error reading referrals from storage");
    return [];
  }
};

/**
 * Save all referrals to secure storage
 */
const saveAllReferrals = (referrals) => {
  try {
    secureStorage.setItem(STORAGE_KEY, referrals);
  } catch (err) {
    console.error("Error saving referrals to storage");
  }
};

/**
 * Get a single referral by ID
 */
export const getReferralById = (id) => {
  const referrals = getAllReferrals();
  return referrals.find((r) => r.id === id) || null;
};

/**
 * Create a new referral
 */
export const createReferral = (referralData) => {
  const referrals = getAllReferrals();
  const newReferral = {
    ...referralData,
    id: generateId(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  referrals.push(newReferral);
  saveAllReferrals(referrals);
  return newReferral;
};

/**
 * Update an existing referral
 */
export const updateReferral = (id, updates) => {
  const referrals = getAllReferrals();
  const index = referrals.findIndex((r) => r.id === id);
  if (index === -1) return null;

  referrals[index] = {
    ...referrals[index],
    ...updates,
    updatedAt: new Date().toISOString(),
  };
  saveAllReferrals(referrals);
  return referrals[index];
};

/**
 * Delete a referral
 */
export const deleteReferral = (id) => {
  const referrals = getAllReferrals();
  const filtered = referrals.filter((r) => r.id !== id);
  saveAllReferrals(filtered);
  return true;
};

/**
 * Add a comment to a referral
 */
export const addComment = (referralId, comment, staffName) => {
  const referrals = getAllReferrals();
  const index = referrals.findIndex((r) => r.id === referralId);
  if (index === -1) return null;

  const newComment = {
    id: `CMT-${Date.now()}`,
    comment,
    staffName,
    timestamp: new Date().toISOString(),
  };

  if (!referrals[index].comments) {
    referrals[index].comments = [];
  }
  referrals[index].comments.push(newComment);
  referrals[index].updatedAt = new Date().toISOString();

  saveAllReferrals(referrals);
  return newComment;
};

/**
 * Get referrals with filters and pagination
 */
export const getReferrals = ({
  center = "All",
  searchText = "",
  status = "",
  followUp = "",
  urgencyLevel = "",
  page = 1,
  pageSize = 15,
} = {}) => {
  let referrals = getAllReferrals();

  // Filter by center
  if (center && center !== "All") {
    referrals = referrals.filter((r) => r.center === center);
  }

  // Filter by search text (member name)
  if (searchText.trim()) {
    const search = searchText.toLowerCase();
    referrals = referrals.filter(
      (r) =>
        r.memberFullName?.toLowerCase().includes(search) ||
        r.memberId?.toLowerCase().includes(search)
    );
  }

  // Filter by status
  if (status) {
    referrals = referrals.filter((r) => r.status === status);
  }

  // Filter by follow up
  if (followUp) {
    referrals = referrals.filter((r) => r.followUp === followUp);
  }

  // Filter by urgency level
  if (urgencyLevel) {
    referrals = referrals.filter((r) => r.urgencyLevel === urgencyLevel);
  }

  // Sort by date (newest first)
  referrals.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Get total count before pagination
  const total = referrals.length;

  // Paginate
  const startIndex = (page - 1) * pageSize;
  const paginatedReferrals = referrals.slice(startIndex, startIndex + pageSize);

  return {
    data: paginatedReferrals,
    total,
    page,
    pageSize,
    totalPages: Math.ceil(total / pageSize),
  };
};

/**
 * Get counts by center
 */
export const getCenterCounts = () => {
  const referrals = getAllReferrals();
  const counts = { All: referrals.length };

  referrals.forEach((r) => {
    if (r.center) {
      counts[r.center] = (counts[r.center] || 0) + 1;
    }
  });

  return counts;
};

/**
 * Initialize storage
 */
export const initializeStorage = () => {
  return getAllReferrals();
};

/**
 * Clear all referrals (for development/testing)
 */
export const clearAllReferrals = () => {
  secureStorage.removeItem(STORAGE_KEY);
};
